<script lang="ts" module>
	import Header from '#blocks/header.svelte'
	import Hero from '#blocks/hero.svelte'
	import LinkList from '#blocks/linklist.svelte'
	import { Storyblok } from '#library/storyblok.svelte.ts'
	import Render from '#components/render.svelte'
	import type { ComponentProps } from 'svelte'
	import Share from '#components/share.svelte'
	import { format_date } from 'kitto'
	import type { ISbStories, ISbStoryData } from '@storyblok/svelte'
	import type { Blog } from '#library/components.schema.build.ts'
	import type { Blog as BlogTemplate } from '#library/types.ts'
	import Featured from '#blocks/featured.svelte'

	// results go on the story, not module scope: module state is shared by every
	// request the server process handles, so one visitor's related posts were
	// served to everyone else. `story` is per-request, so writing there is safe.
	export const load: Load<BlogTemplate> = async ({ story, api, version }) => {
		/** if related stories are available, use them */
		if (story.content.related?.length) {
			story.content.more = story.content.related as unknown as Array<ISbStoryData<Blog>>
			return
		}

		/** Fallback to retrieve 3 other stories (with blog/news type) */
		const {
			data: { stories }
		} = (await api.get('cdn/stories', {
			version,
			content_type: story.content.component,
			per_page: 3,
			resolve_links: 'url',
			excluding_slugs: story.full_slug,
			sort_by: 'first_published_at:desc'
		})) as ISbStories<Blog>

		story.content.more = stories
	}

	interface Props {
		data: Storyblok<BlogTemplate>
	}
</script>

<script lang="ts">
	let { data }: Props = $props()

	const { content, tag_list, ...rest } = $derived(data.story!)
	const date = $derived(rest.first_published_at ?? rest.published_at)
</script>

<article class="article">
	<Header />

	{#if content.image}
		<Hero
			blok={{
				theme: 'theme-crocus-on-light-gimson',
				anchor: 'intro',
				image: content.image
			} as unknown as ComponentProps<typeof Hero>['blok']}
		/>
	{/if}

	<div class="contain meta">
		<div class="tags">
			{tag_list.join(', ')}
		</div>
		{#if date}
			<div class="date">
				{format_date('{DD}/{MM}/{YYYY}', date)}
			</div>
		{/if}
	</div>

	{#if content.content?.length}
		{#each content.content as blok (blok._uid)}
			{#if blok.component === 'rich_text'}
				<div class="contain content">
					<div class="body">
						<Render content={blok.content} />
					</div>
				</div>
			{:else}
				<Storyblok.Block {blok} />
			{/if}
		{/each}
	{/if}

	<Share />
</article>

{#if content.more}
	{@const type = content.component === 'blog' ? 'blogs' : 'news'}
	{@const related = content.related?.length}
	<Featured
		blok={{
			stories: content.more,
			theme: 'theme-crocus-on-gimson',
			title: `${related ? 'Related' : 'More'} ${type}`
		}}
	/>
{/if}

<LinkList
	blok={{
		theme: 'theme-rose-on-white',
		title: 'Discover what we stand for',
		body: 'Bedales champions an innovative student-centred education, prioritising academic freedom, creativity, sustainability and wellbeing. We foster individuality, moral purpose and a lifelong love of learning in a changing world.',
		items: [
			{
				link: {
					text: 'Learning',
					url: '/learning'
				}
			},
			{
				link: {
					text: 'Living',
					url: '/living'
				}
			},
			{
				link: {
					text: 'Community',
					url: '/community'
				}
			},
			{
				link: {
					text: 'Environment',
					url: '/environment'
				}
			}
		]
	} as unknown as ComponentProps<typeof LinkList>['blok']}
/>

<style lang="css">
	.article {
		padding-block-end: var(--s-96);
	}

	.meta {
		display: flex;
		justify-content: space-between;
		margin-block-start: var(--s-32);
	}

	.content {
		display: grid;
		gap: 2rem;
		font: 400 var(--fs-body1);
		letter-spacing: var(--ls-body1);
		padding-block: var(--s-32);

		@media (--from-tablet) {
			grid-template-columns: 3fr 7fr 3fr;
		}
	}

	.body {
		@media (--from-tablet) {
			grid-column-start: 2;
		}
	}

	:global(.body a) {
		font-weight: 600;
	}
</style>
